// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

import PhotoGrid from '../components/PhotoGrid';
import PhotoLightbox from '../components/PhotoLightbox';
import PhotoPickerBar from '../components/PhotoPickerBar';
import { useTourPhotos } from '../hooks/useTourPhotos';
import type { TourPhoto } from '../types';

type TourPhotosScreenProps = {
  tourId: string;
  /** Preselects a round's filter, for entry points that are already scoped to one round. */
  initialRoundId?: string | null;
};

/** Sentinel for the "no round" chip; a real roundId is never an empty string. */
const UNASSIGNED = '__unassigned__';

export default function TourPhotosScreen({ tourId, initialRoundId = null }: TourPhotosScreenProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(initialRoundId);
  const [openPhoto, setOpenPhoto] = useState<TourPhoto | null>(null);

  const { photos, rounds, unassignedCount, loading, error, refresh, deletePhoto, setAsTourImage, setPhotoVisibility } =
    useTourPhotos({
      tourId,
      roundId: selectedRoundId === UNASSIGNED ? null : selectedRoundId,
    });

  // The server filters by round, but not by "has no round" — that would need a tri-state query
  // param. Filtering the unassigned case here keeps the API surface simpler.
  const visiblePhotos = useMemo(
    () => (selectedRoundId === UNASSIGNED ? photos.filter((photo) => photo.roundId === null) : photos),
    [photos, selectedRoundId],
  );

  const totalCount = useMemo(
    () => rounds.reduce((sum, round) => sum + round.photoCount, 0) + unassignedCount,
    [rounds, unassignedCount],
  );

  const handleDelete = async (photo: TourPhoto) => {
    await deletePhoto(photo.id);
  };

  const handleSetVisibility = async (photo: TourPhoto, isPublic: boolean) => {
    const accepted = await setPhotoVisibility(photo.id, isPublic);
    if (accepted) {
      // The lightbox renders from this snapshot, not the hook's list, so the toggle label and the
      // share button only follow the change if the snapshot follows it too.
      setOpenPhoto((previous) => (previous && previous.id === photo.id ? { ...previous, isPublic } : previous));
    }
    return accepted;
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} testID="tour-photos-screen">
      <Text style={styles.title}>{t('tourPhotos.title')}</Text>
      {totalCount > 0 ? (
        <Text style={styles.subtitle}>{t('tourPhotos.count', { count: totalCount })}</Text>
      ) : null}

      <PhotoPickerBar
        tourId={tourId}
        token={user?.token}
        onUploadsSettled={() => void refresh()}
      />


      {/* Only worth showing when there is actually a choice. A single round's gallery would
          otherwise carry an "All / <that round>" pair that filters nothing — tour furniture on
          something that is not a tour. */}
      {rounds.length + (unassignedCount > 0 ? 1 : 0) > 1 ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chips}>
          <Chip
            label={t('tourPhotos.filterAll')}
            active={selectedRoundId === null}
            onPress={() => setSelectedRoundId(null)}
            testID="tour-photos-filter-all"
          />
          {rounds.map((round) => (
            <Chip
              key={round.roundId}
              label={`${round.name?.trim() || round.courseName || t('tourPhotos.unnamedRound')} (${round.photoCount})`}
              active={selectedRoundId === round.roundId}
              onPress={() => setSelectedRoundId(round.roundId)}
              testID={`tour-photos-filter-round-${round.roundId}`}
            />
          ))}
          {unassignedCount > 0 ? (
            <Chip
              label={`${t('tourPhotos.filterUnassigned')} (${unassignedCount})`}
              active={selectedRoundId === UNASSIGNED}
              onPress={() => setSelectedRoundId(UNASSIGNED)}
              testID="tour-photos-filter-unassigned"
            />
          ) : null}
        </ScrollView>
      ) : null}

      {loading ? (
        <View style={styles.stateBox} testID="tour-photos-loading">
          <ActivityIndicator color={appColors.accent.primary} />
        </View>
      ) : error ? (
        <View style={styles.stateBox} testID="tour-photos-error">
          <Text style={styles.errorText}>{t('tourPhotos.loadError')}</Text>
        </View>
      ) : visiblePhotos.length === 0 ? (
        <View style={styles.stateBox} testID="tour-photos-empty">
          <Text style={styles.emptyText}>{t('tourPhotos.empty')}</Text>
          <Text style={styles.emptyHint}>{t('tourPhotos.emptyHint')}</Text>
        </View>
      ) : (
        <PhotoGrid
          photos={visiblePhotos}
          apiBaseUrl={apiBaseUrl}
          token={user?.token}
          onSelect={setOpenPhoto}
        />
      )}

      <PhotoLightbox
        photo={openPhoto}
        apiBaseUrl={apiBaseUrl}
        token={user?.token}
        onClose={() => setOpenPhoto(null)}
        onDelete={handleDelete}
        onSetAsTourImage={(photo) => setAsTourImage(photo.id)}
        onSetVisibility={handleSetVisibility}
      />
    </ScrollView>
  );
}

type ChipProps = {
  label: string;
  active: boolean;
  onPress: () => void;
  testID?: string;
};

function Chip({ label, active, onPress, testID }: ChipProps) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.chip, active && styles.chipActive]}
      accessibilityRole="button"
      // The <Text> child does not become the accessible name on RN-web — the rendered button comes
      // out unlabelled in the accessibility tree — so name the control explicitly.
      accessibilityLabel={label}
      // RN-web 0.21 ignores accessibilityState, so the selected state has to reach the DOM
      // as aria-selected to be visible to tests and assistive tech.
      aria-selected={active}
      testID={testID}
    >
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 12,
    gap: 10,
  },
  title: {
    color: appColors.text.heading,
    fontSize: 20,
    fontWeight: '700',
  },
  subtitle: {
    color: appColors.text.muted,
    fontSize: 13,
  },
  chips: {
    gap: 6,
    paddingVertical: 4,
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
  },
  chipActive: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.subtleBg,
  },
  chipText: {
    color: appColors.text.secondary,
    fontSize: 13,
  },
  chipTextActive: {
    color: appColors.accent.primary,
    fontWeight: '700',
  },
  stateBox: {
    paddingVertical: 32,
    alignItems: 'center',
    gap: 6,
  },
  emptyText: {
    color: appColors.text.secondary,
    fontSize: 15,
  },
  emptyHint: {
    color: appColors.text.muted,
    fontSize: 13,
    textAlign: 'center',
  },
  errorText: {
    color: appColors.status.danger,
    fontSize: 14,
  },
});
